import React, { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material'
import { FieldValues, useForm } from 'react-hook-form';
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import AppSelectList, { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import AppYesOrNoSelector from '../../../../Component/Common/Components/FormValidationInputs/AppYesOrNoSelector';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography'
import { ServiceRequiredAndOwnershipStructureForm, ServiceRequiredValidationSchema } from './ServiceRequiredValidationSchema';
import { CoreStaticDataService } from '../../../../Component/Api/StaticData/CoreStaticDataService';
import { useRecoilValue } from 'recoil';
import { VerificationCompanyAtom } from '../../../../State/VerificationCompany.Atom';
import { VerificationAgencyClientApplicationService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { toast } from 'react-toastify';
import {
    ServiceRequiredMatrix,
    VerificationAgencyClientApplication
} from '../../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import { isNil, isEmpty } from "lodash";
import { CoreEnumLookupDto } from "../../../../Component/Model/Dto/CoreStaticData";
import { OwnershipStructureElement, ScorecardTypeCategory, Service } from "../../../../Component/Model/Enum/BEE123Enums";

interface IServiceRequiredExternalPage {
    token: string;
    setHandleNextCount: (i: number) => void;
    handleNextCount: number;
}
export const ServiceRequiredExternalPage = (props: IServiceRequiredExternalPage) => {
    const verificationCompany = useRecoilValue(VerificationCompanyAtom);
    const [serviceRequiredOptions, setServiceRequiredOptions] = React.useState<IDropDownOptions[]>([]);
    const [BBBEECodeOptions, setBBBEECodeOptions] = React.useState<IDropDownOptions[]>([]);
    const [sectorCodeOptions, setSectorCodeOptions] = React.useState<IDropDownOptions[]>([]);
    const [sectorCodeMatrix, setSectorCodeMatrix] = React.useState<ServiceRequiredMatrix>({});
    const [scorecardTypeOptions, setScorecardTypeOptions] = React.useState<IDropDownOptions[]>([]);
    const [ownershipStructureElementsOptions, setOwnershipStructureElementsOptions] = React.useState<IDropDownOptions[]>([]);
    const [showScorecardType, setShowScorecardType] = React.useState(false);
    const [showNumberOfEntities, setShowNumberOfEntities] = React.useState(false);
    const [serviceRequiredId, setServiceRequiredId] = React.useState(-1);
    const [ownershipStructureId, setOwnershipStructureId] = React.useState(-1);
    const [applicationReferenceNumber, setApplicationReferenceNumber] = React.useState("");
    const [enquiryDate, setEnquiryDate] = React.useState(new Date());
    const [disableOwnershipSelection, setDisableOwnershipSelection] = React.useState(false);
    const [disableSubSector, setDisableSubSector] = React.useState(false);
    const [disableClaimingBlackOwnership, setDisableClaimingBlackOwnership] = React.useState(false);

    const { control, handleSubmit, formState: { isSubmitting }, reset, watch, setValue, getValues } = useForm({
        mode: 'all',
        resolver: yupResolver(ServiceRequiredValidationSchema),
    });

    const selectedBBBEECodes = watch('bbbeeCode');
    const selectedScorecardType = watch('scorecardTypeCategory');
    const selectedOwnershipStructureElements = watch('ownershipStructureElements');


    const showRecordsCentralised = !isNil(watch('isPartOfGroupStructure', null));
    const showClaimingBlackOwnership = !isNil(watch('isRecordsCentralised', null));
    const showStructureElements = !isNil(watch('claimingBlackOwnership', null));

    const resetFormData = (data: VerificationAgencyClientApplication.ServiceRequiredAndOwnershipStructureDto) => {
        const formData = {
            applicationReferenceNumber: data.applicationReferenceNumber,
            dateEnquiryReceived: data.dateEnquiryReceived,
            ...data.ownershipStructure,
            ...data.serviceRequired
        }

        setApplicationReferenceNumber(data.applicationReferenceNumber);
        setEnquiryDate(data.dateEnquiryReceived);
        setOwnershipStructureId(data.ownershipStructure?.ownershipStructureId ?? -1);
        setServiceRequiredId(data.serviceRequired?.serviceRequiredId ?? -1);
        setShowScorecardType(data.serviceRequired?.sectorCode === 0 ? true : !!data.serviceRequired?.sectorCode);
        reset(formData);
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const formData = { ...data } as ServiceRequiredAndOwnershipStructureForm;
            const requestData = {
                vaClientApplicationId: verificationCompany.applicationId,
                dateEnquiryReceived: enquiryDate,
                applicationReferenceNumber: applicationReferenceNumber,
                token: props.token,
                serviceRequired: {
                    serviceRequiredId: serviceRequiredId,
                    serviceRequired: formData.serviceRequired,
                    bbbeeCode: formData.bbbeeCode,
                    sectorCode: formData.sectorCode,
                    scorecardTypeCategory: formData.scorecardTypeCategory,
                    numberOfScorecards: formData.numberOfScorecards,
                    isPartOfGroupStructure: formData.isPartOfGroupStructure,
                    isRecordsCentralised: formData.isRecordsCentralised,
                    numberOfConsolidatedScorecards: formData.numberOfConsolidatedScorecards
                },
                ownershipStructure: {
                    ownershipStructureId: ownershipStructureId,
                    claimingBlackOwnership: formData.claimingBlackOwnership,
                    ownershipStructureElements: formData.ownershipStructureElements,
                    containTrustBboEsop: formData.containTrustBboEsop,
                    percentageBlackOwnershipClaimed: formData.percentageBlackOwnershipClaimed
                },
            } as VerificationAgencyClientApplication.ServiceRequiredAndOwnershipStructureTempAccessDto;

            if (serviceRequiredId < 0 && ownershipStructureId < 0) {
                const response = await VerificationAgencyClientApplicationService.CreateServiceRequiredAndOwnershipStructureExternalPage(requestData);
                resetFormData(response.data);
            }
            else {
                const response = await VerificationAgencyClientApplicationService.UpdateServiceRequiredAndOwnershipStructureExternalPage(requestData);
                resetFormData(response.data);
            }
            props.setHandleNextCount(props.handleNextCount + 1);
            toast.success("Service Required Saved");
        } catch (error) {
        }
    }

    const fetchStaticData = async () => {
        try {
            const serviceRequiredPromise = CoreStaticDataService.getServicesRequired();
            const BBBEECodesPromise = CoreStaticDataService.getBBBEECodes();
            const scorecardTypesPromise = CoreStaticDataService.getScorecardTypeCategories();
            const ownershipStructureElementsPromise = CoreStaticDataService.getOwnershipStructureElements();
            const sectorCodeMatrixPromise = CoreStaticDataService.getServiceRequiredCodeMatrix();

            const [serviceRequired, BBBEECodes, scorecardTypes, ownershipStructureElements, sectorCodeMatrixElements] = await Promise.all([
                serviceRequiredPromise,
                BBBEECodesPromise,
                scorecardTypesPromise,
                ownershipStructureElementsPromise,
                sectorCodeMatrixPromise
            ]);

            const serviceData: IDropDownOptions[] = serviceRequired.data.map(item => {
                return { id: item.id, value: item.name }
            });
            const BBBEECodesData: IDropDownOptions[] = BBBEECodes.data.map(item => {
                return { id: item.id, value: item.name }
            });

            const scorecardTypeData: IDropDownOptions[] = scorecardTypes.data.map(item => {
                return { id: item.id, value: item.name }
            });
            const ownershipStructureElementsData: IDropDownOptions[] = ownershipStructureElements.data.map(item => {
                return { id: item.id, value: item.name }
            });

            setServiceRequiredOptions(serviceData);
            setBBBEECodeOptions(BBBEECodesData);
            setSectorCodeMatrix(sectorCodeMatrixElements.data);
            setScorecardTypeOptions(scorecardTypeData);
            setOwnershipStructureElementsOptions(ownershipStructureElementsData);
        } catch (error) {
        }
    }

    const fetchApplicationServiceRequiredData = async () => {
        try {
            const applicationId = verificationCompany.applicationId;
            if (applicationId !== -1) {
                const response = await VerificationAgencyClientApplicationService.GetServiceRequiredAndOwnershipStructureByApplicationIdExternalPage(applicationId, props.token);
                resetFormData(response.data);
            }
        } catch (error) {
        }
    }

    const handleBeeCodesChange = (selectedValue: number) => {
        if (selectedValue && !isEmpty(sectorCodeMatrix)) {
            try {
                let filteredCodes = sectorCodeMatrix[selectedValue] as unknown as CoreEnumLookupDto[];

                const sectorCodesData: IDropDownOptions[] = filteredCodes.map(item => {
                    return { id: item.id, value: item.name };
                });

                setSectorCodeOptions(sectorCodesData);

                if (sectorCodesData.some(item => item.id === 0)) {
                    setValue('sectorCode', 0, { shouldValidate: true });
                    setDisableSubSector(true)
                }
                else {
                    setDisableSubSector(false)
                }

                setShowScorecardType(true);
            }
            catch (error) {
                setSectorCodeOptions([]);
                setShowScorecardType(false);
            }

        }
        else {
            setSectorCodeOptions([]);
            setShowScorecardType(false);
        }
    }

    useEffect(() => {
        handleBeeCodesChange(selectedBBBEECodes);
    }, [selectedBBBEECodes, sectorCodeMatrix]);

    useEffect(() => {
        if (selectedScorecardType === ScorecardTypeCategory.ConsolidatedGeneric || selectedScorecardType === ScorecardTypeCategory.ConsolidatedQSE) {
            setShowNumberOfEntities(true);
        }
        else {
            setShowNumberOfEntities(false);
            setValue('numberOfConsolidatedScorecards', undefined)
        }
    }, [selectedScorecardType]);

    useEffect(() => {
        let ownershipSelection = getValues('ownershipStructureElements');

        if (ownershipSelection?.some((f: number) =>
            f === OwnershipStructureElement.BroadBasedOwnershipScheme ||
            f === OwnershipStructureElement.EmployeeShareOwnershipProgram ||
            f === OwnershipStructureElement.Trust)) {
            setValue('containTrustBboEsop', 1);
            setDisableOwnershipSelection(true);
        } else {
            setDisableOwnershipSelection(false);
        }
    }, [selectedOwnershipStructureElements]);


    useEffect(() => {
        const selectedServiceRequired = watch('serviceRequired');
        if (selectedServiceRequired === Service.OwnershipReview || selectedServiceRequired === Service.OwnershipCertificate) {
            setValue('claimingBlackOwnership', true);
            setDisableClaimingBlackOwnership(true);
        } else {
            setDisableClaimingBlackOwnership(false);
        }
    }, [watch('serviceRequired')]);

    useEffect(() => {
        fetchStaticData();
        fetchApplicationServiceRequiredData();
    }, [verificationCompany.applicationId]);

    return (
        <>
            <MDTypography color='#000000' variant='h5' fontWeight='bold' >
                Service Required
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px", paddingBottom: "15px", paddingTop: "8px", color: "#727272" }}>
                Indicate the BEE service your business requires, such as BBBEE verification gap review, ownership review, opinion, joint venture, training, SED beneficiary certificate, ownership certificate, or the re-evaluation of element(s).
            </MDTypography>
            <Grid container component="form" id="ServiceRequireForm" onSubmit={handleSubmit(handleDataSubmit)} spacing={2}>
                <Grid item xs={6}>
                    <Box>
                        <Grid container spacing={1.5}>
                            <Grid item xs={10}>
                                <AppSelectList control={control} name="serviceRequired" label="Service Required" items={serviceRequiredOptions} size="small" />
                            </Grid>
                            {watch('serviceRequired') &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="bbbeeCode" label="B-BBEE Codes to Apply" items={BBBEECodeOptions} size="small" />
                                </Grid>}
                            {selectedBBBEECodes &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="sectorCode" label="Sector Codes to Apply" items={sectorCodeOptions} size="small" disabled={disableSubSector}/>
                                </Grid>}
                            {showScorecardType &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="scorecardTypeCategory" label="Type of Scorecard Required" items={scorecardTypeOptions} size="small" />
                                </Grid>}
                            {showNumberOfEntities && <Grid item xs={10}>
                                <AppTextInput control={control} name="numberOfConsolidatedScorecards" label="Number of entities" size="small" />
                            </Grid>}
                            {watch('scorecardTypeCategory') && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="isPartOfGroupStructure" label='Is the company part of a group structure?' size="small" />
                            </Grid>}
                            {showRecordsCentralised && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="isRecordsCentralised" label='Are all records/functions centralised?' size="small" />
                            </Grid>}
                            {showClaimingBlackOwnership && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="claimingBlackOwnership" label='Claiming Black Ownership?' size="small" disabled={disableClaimingBlackOwnership}/>
                            </Grid>}
                            {watch('claimingBlackOwnership') && showClaimingBlackOwnership &&  <Grid item xs={10}>
                                <AppTextInput control={control} name="percentageBlackOwnershipClaimed" label="Percentage Black Ownership Claimed" size="small" />
                            </Grid>}
                            {watch('claimingBlackOwnership') && showStructureElements && showClaimingBlackOwnership && <Grid item xs={10}>
                                <AppMultiSelect control={control} name="ownershipStructureElements" label="Select all elements included in the ownership structure" options={ownershipStructureElementsOptions} size="small" />
                            </Grid>}
                            {disableOwnershipSelection && watch('claimingBlackOwnership') && watch('ownershipStructureElements') && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="containTrustBboEsop" label="Does the Ownership Structure contain Trusts/BBOS's or ESOP's?" size="small" disabled={disableOwnershipSelection} />
                            </Grid>}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
            <Box mt={3} width='100%' display='flex' justifyContent='space-between'>
                <Box>
                    <MDButton variant='outlined' color='secondary' onClick={() => { props.setHandleNextCount(props.handleNextCount - 1); }}>
                        Previous
                    </MDButton>
                </Box>
                <LoadingButton loading={isSubmitting} variant='contained' form='ServiceRequireForm' type="submit" color='primary'>
                    next
                </LoadingButton>
            </Box>

        </>
    )
}
